import {
  loopbackDelete,
  LoopbackFilter,
  loopbackGet,
  loopbackPost,
  loopbackPut,
} from '..';

import {
  DigitalCase,
  DigitalGroundTruth,
  DigitalQuestion,
  DigitalSection,
  DigitalSurvey,
  ExtendedDigitalSurvey,
  DigitalSubmission,
  DigitalSubmissionMeta,
  SurveySubmissionMeta,
  AggregatedSubmissionData,
  AggregatedSubmission,
  QuestionCaseSetting,
  OverviewSurveySubmission,
  DigitalGroundTruthGroup,
  DigitalSectionScore,
  DigitalSurveyScore,
  DigitalQuestionScore,
  PartialBy,
  ToObject,
} from '../../redux/types';
import { constructQueryParams } from './apiUtil';

export const addDigitalCase = (caseData: Omit<DigitalCase, 'id'>): Promise<DigitalCase> => loopbackPost('DigitalCases', caseData);

export const getDigitalCases = (digitalSurveyId: number): Promise<DigitalCase[]> => {
  const filter: LoopbackFilter = {
    where: {
      digitalSurveyId,
    },
  };
  const qp = constructQueryParams(filter);
  return loopbackGet(`DigitalCases${qp}`);
};

export const deleteDigitalCase = (caseId: number): Promise<void> => loopbackDelete(`DigitalCases/${caseId}`);

export const updateDigitalCase = (caseId: number, caseData: DigitalCase): Promise<DigitalCase> => loopbackPut(`DigitalCases/${caseId}`, caseData);

export const addDigitalSection = (payload: Omit<DigitalSection, 'id'>): Promise<DigitalSection> => loopbackPost('DigitalSections', payload);

export const fetchDigitalSurvey = async (surveyId: number): Promise<ExtendedDigitalSurvey> => {
  const filter: LoopbackFilter = {
    include: ['digitalSections', 'digitalQuestions'],
  };
  const params = constructQueryParams(filter);
  return loopbackGet(`DigitalSurveys/${surveyId}${params}`);
};

export const getDigitalSurveyByTrialId = async (trialId: number): Promise<DigitalSurvey[]> => {
  const filter: LoopbackFilter = {
    where: {
      trialId,
    },
  };
  const qp = constructQueryParams(filter);
  return loopbackGet(`DigitalSurveys${qp}`);
};

export const createDigitalSurvey = (payload: Omit<DigitalSurvey, 'id'>): Promise<DigitalSurvey> => loopbackPost('DigitalSurveys', payload);

export const updateDigitalSurvey = (payload: DigitalSurvey): Promise<DigitalSurvey> => loopbackPut('DigitalSurveys', payload);

export const deleteDigitalSection = (sectionId: number): Promise<void> => loopbackGet(`DigitalSections/${sectionId}/deleteSectionWithCases`);

export const updateDigitalSection = (sec: DigitalSection): Promise<DigitalSection> => loopbackPut(`DigitalSections/${sec.id}`, sec);

export const addDigitalQuestion = (payload: Omit<DigitalQuestion, 'id'>): Promise<DigitalQuestion> => loopbackPost('DigitalQuestions', payload);

export const fetchDigitalQuestions = (surveyId: number): Promise<DigitalQuestion[]> => {
  const filter: LoopbackFilter = {
    where: {
      digitalSurveyId: surveyId,
    },
  };
  const queryParams = constructQueryParams(filter);
  return loopbackGet(`DigitalQuestions${queryParams}`);
};

export const updateDigitalQuestion = (payload: DigitalQuestion): Promise<DigitalQuestion> => loopbackPut('DigitalQuestions', payload);

export const deleteDigitalQuestion = (questionId: number): Promise<void> => loopbackDelete(`DigitalQuestions/${questionId}`);

export const deleteDigitalQuestionBy = (arg: BulkDeleteGTArgument): Promise<void> => {
  const filter: LoopbackFilter = {
    where: {
      ...arg,
    },
  };
  const queryParam = constructQueryParams(filter);
  return loopbackGet(`DigitalQuestions/bulkDelete${queryParam}`);
};

export const addOrUpdateDigitalGroundTruth = (gt: PartialBy<DigitalGroundTruth, 'id'>): Promise<DigitalGroundTruth> => loopbackPost('DigitalGroundTruths/addOrUpdateGroundTruth', gt);

export const getGroundtruthBySurveyId = (surveyId: number): Promise<DigitalGroundTruth[]> => loopbackGet(`DigitalGroundTruths/getGroundtruthBySurveyId?surveyId=${surveyId}`);

export const deleteDigitalGroundTruth = (groundTruthId: number): Promise<void> => loopbackDelete(`DigitalGroundTruths/${groundTruthId}`);

type BulkDeleteGTArgument = Partial<Record<'digitalSurveyId' | 'digitalSectionId' | 'digitalCaseId' | 'digitalQuestionId' | 'digitalGroundTruthGroupId', number>>;
export const deleteDigitalGroundTruthBy = (arg: BulkDeleteGTArgument): Promise<void> => {
  const filter: LoopbackFilter = {
    where: {
      ...arg,
    },
  };
  const queryParam = constructQueryParams(filter);
  return loopbackGet(`DigitalGroundTruths/bulkDelete${queryParam}`);
};

export const getExerciseGroundTruth = (surveyId: number): Promise<DigitalGroundTruth[]> => loopbackGet(`DigitalGroundTruths/getExerciseGroundTruth?surveyId=${surveyId}`);

export type DigitalSubmissionRequest = Omit<DigitalSubmission, 'id' | 'enrollmentId'> & {
  trialId: number;
  nonEvaluable?: boolean;
};

export const checkSubmissionExist = (
  query: { digitalCaseId?: number, digitalQuestionId?: number },
): Promise<{ count: number }> => {
  const searchFilter = JSON.stringify(query);
  return loopbackGet(`DigitalSubmissions/count?where=${searchFilter}`);
};

/** End-User to add or update submissions */
export const addOrUpdateDigitalSubmission = (submissionData: DigitalSubmissionRequest): Promise<DigitalSubmission> => loopbackPost('DigitalSubmissions/addOrUpdateSubmission', submissionData);

/** Admin end-point to update DigitalSubmissionSubmission */
export const UpdateDigitalSubmission = (submissionData: DigitalSubmission): Promise<DigitalSubmission> => loopbackPut('DigitalSubmissions', submissionData);

export const updateReviewScore = (
  submissionData: DigitalSubmission,
): Promise<DigitalSubmission> => {
  const { id, reviewScore } = submissionData;
  return loopbackGet(`DigitalSubmissions/${id}/updateReviewScore${reviewScore !== undefined && reviewScore !== null ? `?score=${reviewScore}` : ''}`);
};

export const getUsersDigitalSubmissions = (trialId: number): Promise<DigitalSubmission[]> => loopbackGet(`DigitalSubmissions/getUsersDigitalSubmission?trialId=${trialId}`);

export const getInstituteDigitalSubmission = (trialId: number): Promise<DigitalSubmission[]> => loopbackGet(`DigitalSubmissions/getInstituteDigitalSubmission?trialId=${trialId}`);

export const getDigitalSubmissions = (enrollmentId: number): Promise<DigitalSubmission[]> => {
  const filter: LoopbackFilter = {
    where: {
      enrollmentId,
    },
  };
  const queryParam = constructQueryParams(filter);
  return loopbackGet(`DigitalSubmissions${queryParam}`);
};

export const initializeSubmissionMeta = (trialId: number, sectionId: number): Promise<DigitalSubmissionMeta> => loopbackGet(`DigitalSubmissionMeta/initializeSubmissionMeta?trialId=${trialId}&sectionId=${sectionId}`);

export const findOrCreateSubmissionMetaForAllSections = (trialId: number): Promise<DigitalSubmissionMeta[]> => loopbackGet(`DigitalSubmissionMeta/findOrCreateSubmissionMetaForAllSections?trialId=${trialId}`);

export const getDigitalSubmissionMeta = (trialId: number): Promise<DigitalSubmissionMeta[]> => loopbackGet(`DigitalSubmissionMeta/getDigitalSubmissionMeta?trialId=${trialId}`);

export const getDigitalSubmissionMetaByEnrollment = (enrollmentId: number): Promise<DigitalSubmissionMeta[]> => loopbackGet(`DigitalSubmissionMeta/getDigitalSubmissionMetaByEnrollment?enrollmentId=${enrollmentId}`);

export const finalizeDigitalSubmission = (trialId: number, sectionId: number, drt = false): Promise<DigitalSubmissionMeta> => loopbackGet(`DigitalSubmissionMeta/finalizeDigitalSubmission?trialId=${trialId}&sectionId=${sectionId}&drt=${drt}`);

export const getDigitalSubmissionMetaBySectionId = (
  query: { selectedSectionId: number },
): Promise<DigitalSubmissionMeta[]> => {
  const searchFilter: LoopbackFilter = { where: query };
  const filter = constructQueryParams(searchFilter);
  return loopbackGet(`DigitalSubmissionMeta${filter}`);
};

export const finalizeDigitalContextQuestion = (trialId: number, sectionId: number): Promise<DigitalSubmissionMeta> => loopbackGet(`DigitalSubmissionMeta/finalizeCtxQuestionSubmission?trialId=${trialId}&sectionId=${sectionId}`);

export const checkInTime = (trialId: number, sectionId: number, pause = false): Promise<DigitalSubmissionMeta> => loopbackGet(`DigitalSubmissionMeta/checkIn?trialId=${trialId}&sectionId=${sectionId}&pause=${pause}`);

export const updateDigitalSubmissionMeta = (dsm: DigitalSubmissionMeta): Promise<DigitalSubmissionMeta> => loopbackPut('DigitalSubmissionMeta', dsm);

export const updateDigitalSubmissionMetaReviewComment = (
  dsmId: number, comment?: string,
): Promise<DigitalSubmissionMeta> => loopbackPost(`DigitalSubmissionMeta/${dsmId}/updateReviewComment`, { comment });

export const finalizeSurvey = (enrollmentId: number): Promise<SurveySubmissionMeta> => loopbackGet(`SurveySubmissionMeta/finalize?enrollmentId=${enrollmentId}`);

export const getSurveySubmissionMeta = (enrollmentId: number): Promise<SurveySubmissionMeta[]> => {
  const filter: LoopbackFilter = {
    where: {
      enrollmentId,
    },
  };
  const qp = constructQueryParams(filter);
  return loopbackGet(`SurveySubmissionMeta${qp}`);
};

/**
 * Evaluate one enrollment
 * allowed role: Admin
 */
export const evaluate = (enrollmentId: number): Promise<SurveySubmissionMeta> => loopbackGet(`SurveySubmissionMeta/evaluate?enrollmentId=${enrollmentId}`);

export const evaluateAll = (trialId: number): Promise<(SurveySubmissionMeta | string)[]> => loopbackGet(`SurveySubmissionMeta/evaluateAll?trialId=${trialId}`);

/** reviewer can trigger this */
export const evaluateReviewScore = (enrollmentId: number, sectionId: number, surveyId: number, questionId: number): Promise<[DigitalSubmissionMeta, SurveySubmissionMeta]> => loopbackGet(`SurveySubmissionMeta/evaluateReviewScore?enrollmentId=${enrollmentId}&sectionId=${sectionId}&surveyId=${surveyId}&questionId=${questionId}`);

/** This is for admin to get live aggregated submissions regardless of finalized submissions */
export const getAggregatedSubmissionsLive = (
  trialId: number, groundTruthGroupId: number,
): Promise<AggregatedSubmissionData> => loopbackGet(
  `SurveySubmissionMeta/aggregateSubmission?trialId=${trialId}&gtgId=${groundTruthGroupId}`,
);

export const exportAggregatedSubmission = (
  surveyId: number, groundTruthGroupId: number, finalized = false,
): Promise<any> => loopbackGet(
  `SurveySubmissionMeta/exportAggregatedSubmission?surveyId=${surveyId}&gtgId=${groundTruthGroupId}&finalized=${finalized}`,
);

export const exportCtxAggregatedSubmission = (surveyId: number, finalized = false): Promise<any> => loopbackGet(`SurveySubmissionMeta/exportAggregateContextSubmission?surveyId=${surveyId}&finalized=${finalized}`);

export const releaseSubmissionEvaluation = (trialId: number): Promise<AggregatedSubmission[]> => loopbackGet(`SurveySubmissionMeta/releaseAggregatedEvaluation?trialId=${trialId}`);

export const updateSurveySubmissionMeta = (ssm: SurveySubmissionMeta): Promise<SurveySubmissionMeta> => loopbackPut('SurveySubmissionMeta', ssm);
export const updateSurveySubmissionMetaReviewComment = (
  ssmId: number, comment?: string,
): Promise<SurveySubmissionMeta> => loopbackPost(`SurveySubmissionMeta/${ssmId}/updateReviewComment`, { comment });

export const getAggregatedSubmissions = (
  trialId: number, groundTruthGroupId: number,
): Promise<AggregatedSubmission> => loopbackGet(
  `AggregatedSubmissions/getAggregatedSubmission?trialId=${trialId}&gtgId=${groundTruthGroupId}`,
);

export const getOverviewSurveySubmission = (trialId: number): Promise<OverviewSurveySubmission> => loopbackGet(`SurveySubmissionMeta/getOverview?trialId=${trialId}`);

export type CopySectionRequestType = {
  trialId: number;
  surveyId: number;
  copySectionIds: number[];
}

export type CopyQuestionRequestType = {
  trialId: number;
  surveyId: number;
  sectionId: number;
  copyQuestionIds: number[];
}
export const copyDigitalSectionsToTrial = (payload: CopySectionRequestType): Promise<void> => loopbackPost('DigitalSurveys/copySectionsToTrial', payload);

export const copyDigitalQuestionsToTrial = (payload: CopyQuestionRequestType): Promise<void> => loopbackPost('DigitalSurveys/copyQuestionsToTrial', payload);

export const addQuestionCaseSetting = (setting: PartialBy<Omit<QuestionCaseSetting, 'id'>, 'digitalCaseId'>): Promise<QuestionCaseSetting> => loopbackPost('QuestionCaseSettings', setting);

export const getQuestionCaseSettings = (surveyId: number): Promise<QuestionCaseSetting[]> => {
  const filter: LoopbackFilter = {
    where: {
      digitalSurveyId: surveyId,
    },
  };
  const qp = constructQueryParams(filter);
  return loopbackGet(`QuestionCaseSettings${qp}`);
};

export const updateQuestionCaseSetting = (setting: PartialBy<QuestionCaseSetting, 'digitalCaseId'>): Promise<QuestionCaseSetting> => loopbackPut('QuestionCaseSettings', setting);

type BulkDeleteSettingArgument = Partial<Record<'digitalSurveyId' | 'digitalSectionId' | 'digitalCaseId' | 'digitalQuestionId', number>>;
export const BulkDeleteQuestionCaseSettingBy = (arg: BulkDeleteSettingArgument): Promise<void> => {
  const filter: LoopbackFilter = {
    where: {
      ...arg,
    },
  };
  const queryParam = constructQueryParams(filter);
  return loopbackGet(`QuestionCaseSettings/bulkDelete${queryParam}`);
};

export const bulkSettingsAllCasesPerQuestion = (payload: Omit<QuestionCaseSetting, 'digitalCaseId' | 'id'>): Promise<QuestionCaseSetting[]> => loopbackPost('QuestionCaseSettings/bulkUpdateAllCasesPerQuestion', payload);

export const bulkSettingsAllQuestionsPerCase = (payload: Omit<QuestionCaseSetting, 'digitalQuestionId' | 'id'>): Promise<QuestionCaseSetting[]> => loopbackPost('QuestionCaseSettings/bulkUpdateAllQuestionsPerCase', payload);

export const addDigitalGTGroup = (payload: Omit<DigitalGroundTruthGroup, 'id'>): Promise<DigitalGroundTruthGroup> => loopbackPost('DigitalGroundTruthGroups', payload);

export const updateDigitalGTGroup = (gtGroup: DigitalGroundTruthGroup): Promise<DigitalGroundTruthGroup> => loopbackPut('DigitalGroundTruthGroups', gtGroup);

export const getDigitalGTGroups = (surveyId: number): Promise<DigitalGroundTruthGroup[]> => {
  const filter: LoopbackFilter = {
    where: {
      digitalSurveyId: surveyId,
    },
  };
  const qp = constructQueryParams(filter);
  return loopbackGet(`DigitalGroundTruthGroups${qp}`);
};

export const deleteDigitalGTGroup = (gtgId: number): Promise<void> => loopbackDelete(`DigitalGroundTruthGroups/${gtgId}`);

export const fetchDigitalSectionScores = (surveyId: number): Promise<DigitalSectionScore[]> => {
  const filter: LoopbackFilter = {
    where: {
      digitalSurveyId: surveyId,
    },
  };
  const qp = constructQueryParams(filter);
  return loopbackGet(`DigitalSectionScores${qp}`);
};

export const addOrUpdateDigitalSectionScore = (payload: Omit<DigitalSectionScore, 'id'>): Promise<DigitalSectionScore> => loopbackPost('DigitalSectionScores/addOrUpdateSectionScore', payload);

type DeleteDigitalSectionScoreBy = ToObject<keyof Pick<DigitalSectionScore, 'digitalGroundTruthGroupId' | 'digitalSectionId' | 'digitalSurveyId'>, number>;
export const deleteDigitalSectionScoreBy = (arg: DeleteDigitalSectionScoreBy): Promise<void> => {
  const filter: LoopbackFilter = {
    where: {
      ...arg,
    },
  };
  const qp = constructQueryParams(filter);
  return loopbackGet(`DigitalSectionScores/deleteBy${qp}`);
};

export const fetchDigitalSurveyScores = (surveyId: number): Promise<DigitalSurveyScore[]> => {
  const filter: LoopbackFilter = {
    where: {
      digitalSurveyId: surveyId,
    },
  };
  const qp = constructQueryParams(filter);
  return loopbackGet(`DigitalSurveyScores${qp}`);
};

export const addOrUpdateDigitalSurveyScore = (payload: Omit<DigitalSurveyScore, 'id'>): Promise<DigitalSurveyScore> => loopbackPost('DigitalSurveyScores/addOrUpdateSurveyScore', payload);

export const deleteDigitalSurveyScores = (groundTruthGroupId: number): Promise<void> => loopbackGet(`DigitalSurveyScores/deleteByGroundTruthGroupId?gtGroupId=${groundTruthGroupId}`);

export const fetchDigitalQuestionScores = (surveyId: number): Promise<DigitalQuestionScore[]> => {
  const filter: LoopbackFilter = {
    where: {
      digitalSurveyId: surveyId,
    },
  };
  const qp = constructQueryParams(filter);
  return loopbackGet(`DigitalQuestionScores${qp}`);
};

export const addOrUpdateDigitalQuestionScore = (payload: Omit<DigitalQuestionScore, 'id'>): Promise<DigitalQuestionScore> => loopbackPost('DigitalQuestionScores/addOrUpdateQuestionScore', payload);

type DeleteDigitalQuestionScoreBy = ToObject<keyof Pick<DigitalQuestionScore, 'digitalGroundTruthGroupId' | 'digitalSectionId' | 'digitalSurveyId' | 'digitalQuestionId'>, number>;
export const deleteDigitalQuestionScoreBy = (arg: DeleteDigitalQuestionScoreBy): Promise<void> => {
  const filter: LoopbackFilter = {
    where: {
      ...arg,
    },
  };
  const qp = constructQueryParams(filter);
  return loopbackGet(`DigitalQuestionScores/deleteBy${qp}`);
};

export const optOutFromSection = (trialId: number, sectionId: number, enrollmentId: number, isOptOut: boolean): Promise<DigitalSubmissionMeta> => loopbackGet(`DigitalSubmissionMeta/optOutFromSection?trialId=${trialId}&sectionId=${sectionId}&enrollmentId=${enrollmentId}&isOptOut=${isOptOut}`);

export const exportMammaEvaluation = (
  enrollmentId: number,
): Promise<any> => loopbackGet(`Enrollments/exportMammaEvaluation?enrollmentId=${enrollmentId}`);
